import 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-fullscreen'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import 'leaflet-draw'
import 'proj4leaflet'
import * as styles from './styles'

const WMS_URL = process.env.VUE_APP_WMS_URL

export const initMap = async vm => {
  const baseLayers = getBaseLayers(vm)
  const map = L.map('map', {
    maxBounds: [[-16.5, 35.5], [6.5, 10.5]],
    minZoom: 5,
    maxZoom: 18,
    layers: [baseLayers.OpenStreetMap]
  })
  vm.map = map
  map.setView([5, 20], 5)
  map.addControl(new L.Control.Fullscreen('top-left'))
  addDrawControls(vm)
  const overlays = await getOverlays(vm)
  L.control.layers(baseLayers, overlays).addTo(map)
  const featureGroup = L.featureGroup()
  vm.featureGroup = featureGroup
  map.addLayer(featureGroup)
  if (vm.bboxFilter) {
    addBBoxFilter(vm)
  }
  filterLayers(vm)
  vm.$root.$emit('map-init')
}

export const getBaseLayers = vm => {
  const baselayers = {}
  const osmUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
  const osmAttrib =
    'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
  const osm = new L.TileLayer(osmUrl, {
    attribution: osmAttrib,
    maxZoom: 18
  })
  const imageryUrl =
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
  const imageryAttrib = '<a href="http://www.esri.com/">Esri</a>'
  const imagery = new L.TileLayer(imageryUrl, {
    attribution: imageryAttrib,
    maxZoom: 18
  })
  const topoUrl =
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'
  const topoAttrib = '<a href="http://www.esri.com/">Esri</a>'
  const topo = new L.TileLayer(topoUrl, {
    attriution: topoAttrib,
    maxZoom: 18
  })
  const hybridUrl = 'http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}'
  const hybridAttrib = '<a href="http://www.google.com/">Google</a>'
  const hybrid = new L.TileLayer(hybridUrl, {
    attribution: hybridAttrib,
    maxZoom: 18,
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  })
  baselayers[vm.$i18n.t('map.layers.osm')] = osm
  baselayers[vm.$i18n.t('map.layers.imagery')] = imagery
  baselayers[vm.$i18n.t('map.layers.topo')] = topo
  baselayers[vm.$i18n.t('map.layers.hybrid')] = hybrid

  return baselayers
}

const bindTooltips = (feature, layer) => {
  layer.bindTooltip(feature.properties.name, { sticky: false })
}

export const getOverlays = async vm => {
  const map = vm.map
  const overlays = {}
  const provinceUrl = '/drc/provinces'
  const boundaryUrl = '/drc/boundary'
  const territoryUrl = '/drc/territories'
  let boundary = null
  let provinces = null
  const territories = null
  map.createPane('agriculture')
  map.createPane('boundary')
  map.createPane('provinces')
  map.createPane('territories')
  map.getPane('boundary').style.zIndex = 650
  map.getPane('boundary').style.pointerEvents = 'none'
  map.getPane('provinces').style.zIndex = 200
  map.getPane('provinces').style.pointerEvents = 'none'
  map.getPane('territories').style.zIndex = 250
  // map.getPane("territories").style.pointerEvents = "none";

  let response = null

  response = await vm.axios.get(provinceUrl)
  provinces = L.geoJSON(response.data, {
    style: styles.drc_provinces,
    pane: 'provinces',
    onEachFeature: bindTooltips
  })
  if (isLayerActive('dp')) {
    provinces.addTo(map)
    vm.provinces = true
  }
  provinces.on('add', () => {
    addToLocalStorage('dp')
    vm.provinces = true
  })
  provinces.on('remove', () => {
    removeFromLocalStorage('dp')
    vm.provinces = false
  })

  // response = await vm.axios.get(territoryUrl);
  // territories = L.geoJSON(response.data, {
  //   style: styles.drc_territories,
  //   pane: "territories",
  //   onEachFeature: bindTooltips
  // });

  response = await vm.axios.get(boundaryUrl)
  boundary = L.geoJSON(response.data, {
    style: styles.drc_boundary,
    pane: 'boundary'
  })
  if (isLayerActive('db')) {
    boundary.addTo(map)
    vm.boundary = true
  }
  boundary.on('add', () => {
    addToLocalStorage('db')
    vm.boundary = true
  })
  boundary.on('remove', () => {
    removeFromLocalStorage('db')
    vm.boundary = false
  })

  const mining_areas = L.tileLayer.wms(WMS_URL, {
    layers: 'mining_permit',
    format: 'image/png',
    version: '1.3.0',
    srs: 'EPSG:3857',
    transparent: true,
    opacity: 0.8
  })
  if (isLayerActive('cmc')) {
    mining_areas.addTo(map)
    vm.mining = true
  }
  mining_areas.on('add', () => {
    addToLocalStorage('cmc')
    vm.mining = true
  })
  mining_areas.on('remove', () => {
    removeFromLocalStorage('cmc')
    vm.mining = false
  })
  addFeatureInfoHandler(vm, mining_areas)

  const logging_areas = L.tileLayer.wms(WMS_URL, {
    layers: 'logging',
    format: 'image/png',
    version: '1.3.0',
    srs: 'EPSG:3857',
    transparent: true,
    opacity: 0.6
  })
  if (isLayerActive('clc')) {
    logging_areas.addTo(map)
    vm.logging_areas = true
  }
  logging_areas.on('add', () => {
    addToLocalStorage('clc')
    vm.logging = true
  })
  logging_areas.on('remove', () => {
    removeFromLocalStorage('clc')
    vm.logging = false
  })
  addFeatureInfoHandler(vm, logging_areas)

  const protected_areas = L.tileLayer.wms(WMS_URL, {
    layers: 'protected_areas',
    format: 'image/png',
    version: '1.3.0',
    srs: 'EPSG:3857',
    opacity: 0.6,
    transparent: true
  })
  if (isLayerActive('pa')) {
    protected_areas.addTo(map)
    vm.protectedAreas = true
  }
  protected_areas.on('add', () => {
    addToLocalStorage('pa')
    vm.protectedAreas = true
  })
  protected_areas.on('remove', () => {
    removeFromLocalStorage('pa')
    vm.protectedAreas = false
  })
  addFeatureInfoHandler(vm, protected_areas)

  const ag_areas = L.tileLayer.wms(WMS_URL, {
    layers: 'agriculture',
    format: 'image/png',
    version: '1.3.0',
    srs: 'EPSG:3857',
    transparent: true,
    opacity: 1
  })
  if (isLayerActive('ag')) {
    ag_areas.addTo(map)
    vm.agriculture = true
  }
  ag_areas.on('add', () => {
    addToLocalStorage('ag')
    vm.agriculture = true
  })
  ag_areas.on('remove', () => {
    removeFromLocalStorage('ag')
    vm.agriculture = false
  })
  addFeatureInfoHandler(vm, ag_areas)

  const miningInfo = `<span id="mining-info"><i class="text-info fas fa-info-circle"></i> ${vm.$i18n.t(
    'map.layers.mining'
  )} </span>`
  const loggingInfo = `<span id="logging-info"><i class="text-info fas fa-info-circle"></i> ${vm.$i18n.t(
    'map.layers.logging'
  )}</span>`
  const protectedInfo = `<span id="protected-areas-info"><i class="text-info fas fa-info-circle"></i> ${vm.$i18n.t(
    'map.layers.protected-areas'
  )}</span>`
  const agricultureInfo = `<span id="agriculture-info"><i class="text-info fas fa-info-circle"></i> ${vm.$i18n.t(
    'map.layers.agriculture'
  )}</span>`

  overlays[miningInfo] = mining_areas
  overlays[loggingInfo] = logging_areas
  overlays[protectedInfo] = protected_areas
  overlays[agricultureInfo] = ag_areas
  overlays[vm.$i18n.t('map.layers.boundary')] = boundary
  overlays[vm.$i18n.t('map.layers.provinces')] = provinces
  // overlays[vm.$i18n.t("map.layers.territories")] = territories;
  return overlays
}

export const addDrawControls = vm => {
  const bboxLayer = new L.FeatureGroup({ style: styles.bbox_style })
  const map = vm.map
  map.addLayer(bboxLayer)
  const options = {
    position: 'topleft',
    draw: {
      marker: false,
      polygon: false,
      polyline: false,
      circle: false,
      circlemarker: false,
      rectangle: {
        shapeOptions: styles.bbox_style
        // shapeOptions: {
        //   color: "#cc0033",
        //   weight: 4,
        //   opacity: 0.8
        // }
      }
    }
  }

  L.drawLocal.draw.toolbar.buttons.rectangle = vm.$i18n.t('map.draw.search')
  L.drawLocal.draw.toolbar.actions.title = vm.$i18n.t('map.draw.cancel')
  L.drawLocal.draw.toolbar.actions.text = vm.$i18n.t('map.draw.cancel')
  L.drawLocal.draw.handlers.rectangle.tooltip.start = vm.$i18n.t(
    'map.draw.start'
  )

  const drawControl = new L.Control.Draw(options)
  map.addControl(drawControl)

  map.on('draw:drawstart', function (e) {
    // vm.bbox = null;
    if (vm.bbox) {
      vm.map.removeLayer(vm.bbox)
    } else {
      vm.bbox = null
    }
    bboxLayer.clearLayers()
    vm.$emit('map-reset')
  })

  map.on('draw:created', function (e) {
    const layer = e.layer
    bboxLayer.addLayer(layer)
    vm.bbox = bboxLayer
    const bbox = layer.toGeoJSON()
    vm.$emit('map-filtered', bbox)
  })

  vm.$root.$on('clear-filters', () => {
    if (vm.bbox) {
      vm.bbox.clearLayers()
    }
    bboxLayer.clearLayers()
    vm.bbox = null
  })
}

export const addBBoxFilter = vm => {
  const bboxLayer = L.geoJSON(vm.bboxFilter, {
    style: styles.bbox_style
  })
  vm.map.addLayer(bboxLayer)
  vm.map.fitBounds(bboxLayer.getBounds(), 20)
  vm.bbox = bboxLayer
}

export const filterLayers = vm => {
  const filteredItems = vm.filteredItems
  if (vm.featureGroup) {
    vm.featureGroup.clearLayers()
  }
  if (filteredItems.length && vm.featureGroup) {
    for (const app of filteredItems) {
      const fg = L.geoJSON(app.locations, {
        style: function (feature) {
          if (feature) {
            return getLocationStyle(feature, vm.is_authenticated)
          }
        },
        pointToLayer: function (feature, latlng) {
          return L.circleMarker(latlng, {
            radius: 9,
            fillColor: '#0ED029',
            opacity: 1,
            fillOpacity: 0.8,
            color: 'blue'
          })
        }
      })
      fg.bindTooltip(app.name)
      vm.featureGroup.addLayer(fg)
      addLayerEvents(vm, fg)
    }
    if (vm.bbox) {
      vm.map.fitBounds(vm.bbox.getBounds())
    } else {
      let bounds = vm.featureGroup.getBounds()
      if (!bounds.isValid()) {
        bounds = vm.map.options.maxBounds
      }
      vm.map.fitBounds(bounds)
    }
  } else {
    vm.map.fitBounds(vm.map.options.maxBounds)
  }
}

export const getLocationStyle = (location, isAuthenticated = false) => {
  const application = location.properties.application
  const overlaps = location.properties.overlaps
  const status = application.concession_status
  const approved = application.smp_approved
  let locationFillColor = null
  if (status === '0') {
    locationFillColor = styles.locationColor
  }
  if (status === '2') {
    locationFillColor = '#fb9b54'
  }
  if (status === '3' || status === 'None' || status === null) {
    locationFillColor = 'blue'
  }
  if (approved) {
    locationFillColor = '#117f69'
  }

  let borderColor = locationFillColor

  if (isAuthenticated) {
    borderColor = overlaps ? 'red' : locationFillColor
  }

  const locationStyle = {
    color: borderColor,
    weight: 1.5,
    opacity: 1,
    fill: true,
    fillColor: locationFillColor,
    fillOpacity: 0.6
  }
  return locationStyle
}

export const addLayerEvents = (vm, layerGroup, popover) => {
  const map = vm.map
  layerGroup.on('mouseover', function (e) {
    this.bringToFront()
    this.setStyle(styles.highlight_style)
    const app = e.layer.feature.properties.application
    vm.$root.$emit('feature-hovered', app)
  })
  layerGroup.on('mouseout', function (e) {
    const app = e.layer.feature.properties.application
    this.setStyle(getLocationStyle(e.layer.feature, vm.is_authenticated))
    vm.$root.$emit('feature-unhovered', app)
  })
  layerGroup.on('click', function (e) {
    if (!map.isFullscreen()) {
      e.originalEvent.preventDefault()
      const id = e.layer.feature.properties.application.id
      if (vm.filterUrl) {
        window.open('/applications/' + id + '/?' + vm.filterUrl, '_blank')
      } else {
        window.open('/applications/' + id + '/', '_blank')
      }
    }
  })
}

/** handle hover events on application list table.**/
export const hoverLayer = (vm, id) => {
  highlightLocation(vm.map, id)
}

export const unhoverLayer = (vm, id) => {
  resetLayerStyle(vm.map, vm.is_authenticated, id)
}

/** highlight locations.**/
export const highlightLocation = (map, id) => {
  map.eachLayer(function (layer) {
    if (
      layer.hasOwnProperty('feature') &&
      layer.feature.properties.hasOwnProperty('application')
    ) {
      const app = layer.feature.properties.application
      if (app.id == id) {
        layer.bringToFront()
        layer.setStyle(styles.highlight_style)
      }
    }
  })
}

/** reset layer styles.**/
export const resetLayerStyle = (map, isAuthenticated, id) => {
  map.eachLayer(function (layer) {
    if (
      layer.hasOwnProperty('feature') &&
      layer.feature.properties.hasOwnProperty('application')
    ) {
      const app = layer.feature.properties.application
      if (app.id == id) {
        layer.setStyle(getLocationStyle(layer.feature, isAuthenticated))
      }
    }
  })
}

const addToLocalStorage = layer => {
  if (localStorage) {
    localStorage.setItem(layer, true)
  }
}

const removeFromLocalStorage = layer => {
  if (localStorage) {
    localStorage.removeItem(layer)
  }
}

const isLayerActive = layer => {
  if (localStorage) {
    return localStorage.getItem(layer) === 'true'
  }
}

const crs_3857 = new L.Proj.CRS(
  'EPSG:3857',
  '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
)

const cl_layers = {
  mining_permit: 'cmc',
  logging: 'clc',
  protected_areas: 'pa',
  agriculture: 'ag'
}

const addFeatureInfoHandler = (vm, layer) => {
  const map = vm.map
  map.on('click', e => {
    // Build the URL for a GetFeatureInfo
    const url = getFeatureInfoUrl(map, layer, e.latlng)
    if (localStorage) {
      const name = layer.options.layers
      if (localStorage.getItem(cl_layers[name])) {
        const response = vm.axios.get(url).then(response => {
          const d = response.data
          if (d.features.length == 0) return
          const props = d.features[0].properties
          const templ = composePopupContent(props)
          L.popup()
            .setLatLng(e.latlng)
            .setContent(templ)
            .openOn(map)
        })
      }
    }
  })
}

const getFeatureInfoUrl = (map, layer, latlng, params) => {
  const point = map.latLngToContainerPoint(latlng, map.getZoom())
  const size = map.getSize()
  const bounds = map.getBounds()
  var sw = bounds.getSouthWest()
  var ne = bounds.getNorthEast()
  var sw = crs_3857.projection._proj.forward([sw.lng, sw.lat])
  var ne = crs_3857.projection._proj.forward([ne.lng, ne.lat])

  const defaultParams = {
    request: 'GetFeatureInfo',
    service: 'WMS',
    srs: 'EPSG:3857',
    styles: '',
    version: '1.1.0',
    format: layer.options.format,
    bbox: [sw.join(','), ne.join(',')].join(','),
    height: size.y,
    width: size.x,
    layers: layer.options.layers,
    query_layers: layer.options.layers,
    info_format: 'application/geojson'
  }

  params = L.Util.extend(defaultParams, params || {})

  params[params.version === '1.3.0' ? 'i' : 'x'] = point.x
  params[params.version === '1.3.0' ? 'j' : 'y'] = point.y

  return layer._url + L.Util.getParamString(params, layer._url, true)
}

const composePopupContent = props => {
  const include_props = [
    'Nomaireprotégée',
    'Nomorigine',
    'Nomsociété',
    'Anciennom',
    'CatégorieUICN',
    'Description',
    'Descrption',
    'Province',
    'Statutlégal',
    'Typedepermis',
    'Statutpermis',
    'Référence',
    'Datededemande',
    'Datedexpiration',
    'Datedattribution',
    'Origineducapital',
    'Numérocontratconcessionforestière'
  ]
  const info = {}
  for (const prop in props) {
    if (include_props.indexOf(String(prop)) > -1) {
      const val =
        props[prop] === 'Null' || props[prop] === ' ' ? ' -- ' : props[prop]
      info[prop] = val
    }
  }
  const template = document.getElementById('featureInfoTemplate').innerHTML
  const templ = _.template(template)({ items: info })
  return templ
}
